import React, { useState, useEffect } from 'react';
import { PROMOCODE_CONFIG, PromocodeHelpers } from '../../config/promocode.js';

const PlatformManager = () => {
  const [platforms, setPlatforms] = useState([]);
  const [showAddPlatform, setShowAddPlatform] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [platformForm, setPlatformForm] = useState({
    name: '',
    code: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    loadPlatforms();
  }, []);

  const loadPlatforms = () => {
    // Load ALL platforms (both active and inactive) for management
    const allPlatforms = Object.entries(PROMOCODE_CONFIG.platforms)
      .map(([key, platform]) => ({ key, ...platform }));
    setPlatforms(allPlatforms);
  };

  const handleAddPlatform = () => {
    setPlatformForm({
      name: '',
      code: '',
      description: '',
      isActive: true
    });
    setShowAddPlatform(true);
    setEditingPlatform(null);
  };

  const handleEditPlatform = (platform) => {
    setPlatformForm({
      name: platform.name,
      code: platform.code,
      description: platform.description,
      isActive: platform.isActive
    });
    setEditingPlatform(platform);
    setShowAddPlatform(true);
  };

  const handleSavePlatform = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Validate platform data
      if (!platformForm.name || !platformForm.code) {
        setMessage('Platform name and code are required');
        setMessageType('error');
        setLoading(false);
        return;
      }

      // Check if code already exists
      const existingPlatform = PromocodeHelpers.getPlatformByCode(platformForm.code);
      if (existingPlatform && (!editingPlatform || existingPlatform.name !== editingPlatform.name)) {
        setMessage('Platform code already exists');
        setMessageType('error');
        setLoading(false);
        return;
      }

      // For now, we'll just update the local state
      // In a real implementation, you'd save to a database
      const newPlatform = {
        key: platformForm.name.toLowerCase(),
        name: platformForm.name,
        code: platformForm.code.toUpperCase(),
        description: platformForm.description,
        isActive: platformForm.isActive
      };

      if (editingPlatform) {
        // Update existing platform in PROMOCODE_CONFIG
        PromocodeHelpers.addPlatform(editingPlatform.key, newPlatform);
        setMessage('Platform updated successfully');
      } else {
        // Add new platform to PROMOCODE_CONFIG
        PromocodeHelpers.addPlatform(newPlatform.key, newPlatform);
        setMessage('Platform added successfully');
      }

      // Reload platforms from PROMOCODE_CONFIG
      loadPlatforms();
      
      setMessageType('success');
      setShowAddPlatform(false);
      setEditingPlatform(null);
    } catch (error) {
      setMessage('Failed to save platform');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlatform = (platform) => {
    if (window.confirm(`Are you sure you want to delete ${platform.name}?`)) {
      // Remove platform from PROMOCODE_CONFIG
      PromocodeHelpers.removePlatform(platform.key);
      
      // Reload platforms from PROMOCODE_CONFIG
      loadPlatforms();
      
      setMessage('Platform deleted successfully');
      setMessageType('success');
    }
  };

  const handleTogglePlatform = (platform) => {
    // Update platform in PROMOCODE_CONFIG
    const updatedPlatform = { ...platform, isActive: !platform.isActive };
    PromocodeHelpers.addPlatform(platform.key, updatedPlatform);
    
    // Reload platforms from PROMOCODE_CONFIG
    loadPlatforms();
    
    setMessage(`Platform ${platform.isActive ? 'deactivated' : 'activated'} successfully`);
    setMessageType('success');
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Platform Management
        </h3>
        <button
          onClick={handleAddPlatform}
          className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Add Platform
        </button>
      </div>

      {/* Platform List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {platforms.map(platform => (
          <div key={platform.key} className="border rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-semibold text-gray-900">{platform.name}</h4>
              <span className={`px-2 py-1 text-xs rounded-full ${
                platform.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {platform.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-2">Code: {platform.code}</p>
            <p className="text-sm text-gray-500 mb-4">{platform.description}</p>
            <div className="flex space-x-2">
              <button
                onClick={() => handleEditPlatform(platform)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Edit
              </button>
              <button
                onClick={() => handleTogglePlatform(platform)}
                className={`text-sm ${
                  platform.isActive 
                    ? 'text-orange-600 hover:text-orange-800' 
                    : 'text-green-600 hover:text-green-800'
                }`}
              >
                {platform.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={() => handleDeletePlatform(platform)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Platform Modal */}
      {showAddPlatform && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h4 className="text-lg font-semibold mb-4">
              {editingPlatform ? 'Edit Platform' : 'Add New Platform'}
            </h4>
            
            <form onSubmit={handleSavePlatform}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform Name
                  </label>
                  <input
                    type="text"
                    value={platformForm.name}
                    onChange={(e) => setPlatformForm({...platformForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform Code (2 letters)
                  </label>
                  <input
                    type="text"
                    value={platformForm.code}
                    onChange={(e) => setPlatformForm({...platformForm, code: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={2}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={platformForm.description}
                    onChange={(e) => setPlatformForm({...platformForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={platformForm.isActive}
                    onChange={(e) => setPlatformForm({...platformForm, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>
              
              <div className="flex space-x-4 mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPlatform(false);
                    setEditingPlatform(null);
                  }}
                  className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-3 rounded-md ${
          messageType === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Information */}
      <div className="mt-6 text-xs text-gray-500">
        <p>• Platform codes are used in promocode generation</p>
        <p>• Each platform can have multiple promocodes</p>
        <p>• Inactive platforms cannot be used for new promocodes</p>
        <p>• Platform codes must be unique and 2 characters long</p>
      </div>
    </div>
  );
};

export default PlatformManager;
